import { FormInput, Tooltip } from '@systemeio/ui'
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum'
import { useShareHash } from 'modules/share/hooks/use-share-hash'
import { getShareUrl } from 'modules/share/utils/get-share-url'
import {
  WORKFLOWS_API,
  createWorkflow,
  removeWorkflow,
  updateWorkflow,
} from 'modules/workflows/api/workflows-api'
import WorkflowActivationModal from 'modules/workflows/components/workflow-activation-modal'
import { WorkflowsLoco } from 'modules/workflows/contants/workflows-loco'
import { useWorkflows } from 'modules/workflows/hooks/use-workflows'
import {
  WorkFlowCreateInterface,
  WorkflowErrorsInterface,
  WorkflowInterface,
} from 'modules/workflows/types/workflow-interface'
import { NextPage } from 'next'
import React, { useState } from 'react'
import { CreateDataType } from 'shared/components/create-modal'
import LinkShareModal from 'shared/components/link-share-modal'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { fetchWithErrors } from 'shared/utils/fetch-with-errors'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getWorkflowEditorUrl } from 'shared/utils/get-workflow-editor-url'
import { CheckCircleIcon } from '@systemeio/ui/icons'
import { useOnceTrue } from 'shared/hooks/use-once-true'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { GrowthbookFlagsEnum } from 'growthbook/types'

const defaultValues: WorkFlowCreateInterface = {
  name: '',
}

const defaultErrors: WorkflowErrorsInterface = {
  name: '',
}

const Workflow: NextPage = () => {
  const { setPagination, pagination, setLimit, limit } = usePagination()
  const workflowSharingIsOn = useFeatureIsOn(GrowthbookFlagsEnum.WorkflowSharing)

  const [errors, setErrors] = useState<WorkflowErrorsInterface>(defaultErrors)

  const { workflows, mutate, counter } = useWorkflows({ limit, pagination })
  const updateData = useUpdateData({
    setPagination,
    api: WORKFLOWS_API,
  })
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const columns: ColumnDefinitionInterface<WorkflowInterface>[] = [
    {
      key: 'name',
      header: t('global.title'),
      widthPercent: 50,
      onRender: workflow => (
        <div className="truncate">
          <a
            rel="noreferrer"
            className="primary-link w-full"
            href={getWorkflowEditorUrl(workflow.id)}
          >
            {workflow.name}
          </a>
        </div>
      ),
    },
    {
      key: 'active',
      header: t('global.status'),
      widthPercent: 20,
      onRender: workflow => (
        <div className="flex items-center lg:ml-4">
          <Tooltip
            label={workflow.active ? t('global.active') : t('global.inactive')}
            mode="hover"
            key="status-tooltip"
          >
            <CheckCircleIcon
              className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${
                workflow.active && 'fill-green'
              }`}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 30,
      onRender: workflow => (
        <span>
          {dateTimeFormat(workflow.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  const onRemoveWorkflow = async (workflow: WorkflowInterface) => {
    await removeWorkflow(workflow.id)
    await updateData(false, workflows)
  }

  const onUpdateWorkflow = async <T,>(
    workflow: WorkflowInterface,
    newData: WorkFlowCreateInterface,
  ) => {
    await fetchWithErrors(async () => {
      await updateWorkflow(workflow.id, newData)
      await updateData(false, undefined, {
        mutationData: workflows,
        onMutate: async () => {
          await mutate(data => {
            if (data) {
              return {
                items: data.items.map(item => {
                  if (item.id === workflow.id) item = { ...item, ...newData }
                  return item
                }),
                hasMore: data.hasMore,
              }
            }
          }, false)
        },
      })
    }, setErrors)
  }

  const onCreate = async (data: CreateDataType<WorkFlowCreateInterface>) => {
    await fetchWithErrors(async () => {
      await createWorkflow(data)
      await updateData(true, workflows)
    }, setErrors)
  }

  const onModalClose = () => setErrors(defaultValues)

  return (
    <ListingLayout
      data={workflows}
      counter={counter}
      title={t('header.top_menu.workflows')}
      type={'compact'}
      createData={{
        caption: t('dashboard.workflow.title'),
        onAfterLeave: onModalClose,
        defaultValues: defaultValues,
        requiredFields: ['name'],
        onCreate: data => onCreate(data),
        creatable: [
          {
            field: 'name',
            onRender: (value, onChange) => (
              <FormInput
                key="workflow-name-create"
                value={value}
                label={t('global.name')}
                onChange={e => {
                  onChange(e.target.value)
                }}
                maxLength={256}
                error={errors.name}
              />
            ),
          },
        ],
      }}
    >
      <Table
        caption={t('dashboard.workflow.title')}
        data={workflows}
        columns={columns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actions={{
          settings: {
            fullCaption: t('dashboard.workflow.edit_workflow'),
            onModalClose: onModalClose,
            editable: [
              {
                field: 'name',
                onRender: (value, onChange) => (
                  <FormInput
                    key="workflow-name-edit"
                    label={t('global.name')}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    error={errors.name}
                  />
                ),
              },
            ],
            onEdit: (workflow, data) => onUpdateWorkflow(workflow, data),
          },
          remove: {
            onChange: workflow => onRemoveWorkflow(workflow),
          },
          edit: {
            setHref: workflow => getWorkflowEditorUrl(workflow.id),
          },
          custom: [
            {
              caption: t('dashboard.actions.title.activate'),
              name: 'activate-workflow',
              shouldRender: workflow => !workflow.active,
              onActionRender: (opened, onClose, instance, key) => (
                <WorkflowActivationModal
                  opened={opened}
                  onClose={onClose}
                  workflow={instance}
                  workflows={workflows}
                  mutate={mutate}
                  key={key}
                  updateData={updateData}
                />
              ),
            },
            {
              caption: t('dashboard.actions.title.deactivate'),
              name: 'deactivate-workflow',
              shouldRender: workflow => workflow.active,
              onActionRender: (opened, onClose, instance, key) => (
                <WorkflowActivationModal
                  opened={opened}
                  onClose={onClose}
                  workflow={instance}
                  workflows={workflows}
                  mutate={mutate}
                  key={key}
                  updateData={updateData}
                />
              ),
            },
            {
              name: 'share',
              caption: t('dashboard.actions.title.share'),
              shouldRender: () => workflowSharingIsOn,
              onActionRender: function ShareActionRender(opened, onClose, workflow, key) {
                const allowFetch = useOnceTrue(!!opened)
                const { hash } = useShareHash(
                  { resourceId: String(workflow.id), resourceType: ShareResourceEnum.Workflow },
                  allowFetch,
                )

                return (
                  <LinkShareModal
                    opened={opened}
                    link={hash && getShareUrl(ShareResourceEnum.Workflow, hash.hash)}
                    isLoading={!hash}
                    title={t('dashboard.workflow.share_workflow_title')}
                    label={t('dashboard.workflow.share_workflow_label')}
                    onClose={onClose}
                    key={key}
                  />
                )
              },
            },
          ],
        }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(WorkflowsLoco, 'header.top_menu.workflows')

export default Workflow
